import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Button } from 'foundations-components/transitional/components/Button';
import { getAssetBusinessTypeTranslationKey, getAssetPageFeatureTranslationKey, getAssetPageTypeTranslationKey } from 'marketplace-ui-assets-core/utils/i18n';
import MobilePanel from 'marketplace-ui-common/components/mobile/MobilePanel';
import MobilePanelBody from 'marketplace-ui-common/components/mobile/MobilePanelBody';
import MobilePanelFooter from 'marketplace-ui-common/components/mobile/MobilePanelFooter';
import MobilePanelHeader from 'marketplace-ui-common/components/mobile/MobilePanelHeader';
import MobilePanelNav from 'marketplace-ui-common/components/mobile/MobilePanelNav';
import PlanCompatibilityFilter from 'marketplace-ui-common/filters/components/PlanCompatibilityFilter';
import { useFocusTrap } from 'marketplace-ui-common/hooks/useFocusTrap';
import { getTierI18nKey } from 'marketplace-ui-common/utils/i18n';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_ASSET_SORT_VALUE } from '../../../common/constants/AssetQuery/AssetQuery';
import { useAssetSortOptions } from '../../../hooks/useAssetSortOptions';
import { useModuleCategories } from '../../../modules/hooks/useModuleCategories';
import { getIsInApp } from '../../../selectors/app';
import { AssetSortToggleGroup } from './AssetSortInput';
import BusinessTypeFilter from './BusinessTypeFilter';
import FreeSupportFilter from './FreeSupportFilter';
import MobileFilterFieldPanel from './MobileFilterFieldPanel';
import ModuleCategoryFilter from './ModuleCategoryFilter';
import OfferingTypeFilter from './OfferingTypeFilter';
import PageFeatureFilter from './PageFeatureFilter';
import PageTypeFilter from './PageTypeFilter';
import PriceFilter, { PriceFilterSubtitle } from './PriceFilter';
import ProviderFilter from './ProviderFilter';
import RatingFilter from './RatingFilter';
import SearchTagFilter from './SearchTagFilter';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function MobileThemeFilters({
  isFilterDisabled = () => false,
  filters,
  onApply,
  onFilterClose,
  onMultiSelectChange
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MobileFilterFieldPanel, {
      onApply: onApply,
      onClose: onFilterClose,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.assets.filters.businessType.title"
      }),
      subtitle: /*#__PURE__*/_jsx(FormattedList, {
        children: filters.businessTypes.map(value => /*#__PURE__*/_jsx(FormattedMessage, {
          message: getAssetBusinessTypeTranslationKey(value)
        }, value))
      }),
      children: /*#__PURE__*/_jsx(BusinessTypeFilter, {
        disabled: isFilterDisabled('businessTypes'),
        onChange: onMultiSelectChange('businessTypes'),
        scrollable: false,
        value: filters.businessTypes
      })
    }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
      onApply: onApply,
      onClose: onFilterClose,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.assets.filters.pageTypes.title"
      }),
      subtitle: /*#__PURE__*/_jsx(FormattedList, {
        children: filters.pageTypes.map(value => /*#__PURE__*/_jsx(FormattedMessage, {
          message: getAssetPageTypeTranslationKey(value)
        }, value))
      }),
      children: /*#__PURE__*/_jsx(PageTypeFilter, {
        disabled: isFilterDisabled('pageTypes'),
        onChange: onMultiSelectChange('pageTypes'),
        scrollable: false,
        value: filters.pageTypes
      })
    }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
      onApply: onApply,
      onClose: onFilterClose,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.assets.filters.pageFeatures.title"
      }),
      subtitle: /*#__PURE__*/_jsx(FormattedList, {
        children: filters.pageFeatures.map(value => /*#__PURE__*/_jsx(FormattedMessage, {
          message: getAssetPageFeatureTranslationKey(value)
        }, value))
      }),
      children: /*#__PURE__*/_jsx(PageFeatureFilter, {
        disabled: isFilterDisabled('pageFeatures'),
        onChange: onMultiSelectChange('pageFeatures'),
        scrollable: false,
        value: filters.pageFeatures
      })
    })]
  });
}
function MobileModuleFilters({
  isFilterDisabled = () => false,
  filters,
  onApply,
  onFilterClose,
  onMultiSelectChange
}) {
  const {
    getCategoryDisplayName
  } = useModuleCategories();
  const categoryDisplayNames = (() => {
    try {
      return filters.categories.map(name => getCategoryDisplayName(name));
    } catch (_unused) {
      return [];
    }
  })();
  return /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
    onApply: onApply,
    onClose: onFilterClose,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.assets.filters.categories.title"
    }),
    subtitle: /*#__PURE__*/_jsx(FormattedList, {
      children: categoryDisplayNames
    }),
    children: /*#__PURE__*/_jsx(ModuleCategoryFilter, {
      disabled: isFilterDisabled('categories'),
      onChange: onMultiSelectChange('categories'),
      scrollable: false,
      value: filters.categories
    })
  });
}
function isThemeFilters(filters) {
  return 'businessTypes' in filters;
}
function isModuleFilters(filters) {
  return 'categories' in filters;
}
export default function MobileFilterPanel({
  filters: defaultFilters,
  isUnifiedSearch = false,
  isFilterDisabled = () => false,
  offeringType,
  onApplyFilters,
  onClearFilters,
  onClose,
  open
}) {
  const [filters, setFilters] = useState(defaultFilters);

  /* Keep filters in sync */
  useEffect(() => setFilters(defaultFilters), [defaultFilters]);
  const isInApp = useSelector(getIsInApp);
  const appContext = isInApp ? 'app' : 'public';
  const sortValue = filters.sortFields[0] || DEFAULT_ASSET_SORT_VALUE;
  const assetSortOptions = useAssetSortOptions(isUnifiedSearch);
  const sortOption = assetSortOptions.find(({
    value
  }) => sortValue === value);
  const sortTitle = sortOption && sortOption.text;
  const handleFieldChange = (field, value) => setFilters(Object.assign({}, filters, {
    [field]: value
  }));
  const handleMultiSelectChange = field => event => setFilters(Object.assign({}, filters, {
    [field]: event.target.value
  }));
  const handlePlanCompatibilityChange = event => {
    const {
      portalCompatible,
      tiers
    } = event.target.value;
    setFilters(Object.assign({}, filters, {
      portalCompatible,
      tiers
    }));
  };
  const filterPanelButtonRef = useRef(null);
  const focusOnFilterPanelButton = () => {
    filterPanelButtonRef.current.focus();
  };
  useFocusTrap(open);
  return /*#__PURE__*/_jsxs(MobilePanel, {
    align: "bottom",
    open: open,
    children: [/*#__PURE__*/_jsx(MobilePanelHeader, {
      itemLeft: /*#__PURE__*/_jsx(Button, {
        autoFocus: true,
        buttonRef: filterPanelButtonRef,
        padding: "400",
        marginInlineStart: "200",
        use: "transparent",
        onClick: onClose,
        tabIndex: 0,
        children: /*#__PURE__*/_jsx("div", {
          role: "button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.filterPanel.cancel"
          })
        })
      }),
      itemRight: /*#__PURE__*/_jsx(Button, {
        marginInlineEnd: "300",
        use: "transparent",
        onClick: () => onApplyFilters(filters),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.filterPanel.apply"
        })
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.filterPanel.header"
      })
    }), /*#__PURE__*/_jsxs(MobilePanelBody, {
      children: [/*#__PURE__*/_jsx(MobilePanelNav, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.filterPanel.sortHeader"
        }),
        children: /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          header: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.filterPanel.sortHeader"
          }),
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: sortTitle,
          children: /*#__PURE__*/_jsx(AssetSortToggleGroup, {
            onSelect: value => handleFieldChange('sortFields', [value]),
            value: sortValue
          })
        })
      }), /*#__PURE__*/_jsxs(MobilePanelNav, {
        title: unescapedText('marketplace-assets-ui.themes.filterPanel.filterHeader'),
        children: [filters.search && /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.offeringType.title"
          }),
          children: /*#__PURE__*/_jsx(OfferingTypeFilter, {
            disabled: isFilterDisabled('offeringTypes'),
            name: "mobile_offeringType",
            onChange: (field, value) => handleFieldChange(field, value),
            value: filters.offeringTypes
          })
        }), isThemeFilters(filters) && /*#__PURE__*/_jsx(MobileThemeFilters, {
          filters: filters,
          onApply: () => onApplyFilters(filters),
          onFilterClose: () => focusOnFilterPanelButton(),
          onMultiSelectChange: handleMultiSelectChange
        }), isModuleFilters(filters) && /*#__PURE__*/_jsx(MobileModuleFilters, {
          filters: filters,
          onApply: () => onApplyFilters(filters),
          onFilterClose: () => focusOnFilterPanelButton(),
          onMultiSelectChange: handleMultiSelectChange
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.portalCompatible.title"
          }),
          subtitle: filters.portalCompatible ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.portalCompatible.subtitle"
          }) : /*#__PURE__*/_jsx(FormattedList, {
            children: filters.tiers.map(tier => /*#__PURE__*/_jsx(FormattedMessage, {
              message: getTierI18nKey(tier)
            }, tier))
          }),
          children: /*#__PURE__*/_jsx(PlanCompatibilityFilter, {
            appContext: appContext,
            ariaLabel: unescapedText('marketplace-assets-ui.assets.filters.portalCompatible.title'),
            onChange: handlePlanCompatibilityChange,
            value: {
              portalCompatible: !!filters.portalCompatible,
              tiers: filters.tiers
            },
            validTierGroups: ['CRM', 'MARKETING', 'CMS']
          })
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.price.title"
          }),
          subtitle: /*#__PURE__*/_jsx(PriceFilterSubtitle, {
            planTypeValue: filters.planType,
            priceRangeValue: filters.priceRange
          }),
          children: /*#__PURE__*/_jsx(PriceFilter, {
            disabled: isFilterDisabled('planType')
            // must be a different name than the desktop PriceFilter
            ,
            name: "mobile_planType",
            onPlanTypeChange: handleFieldChange,
            onPriceRangeChange: handleFieldChange,
            planTypeValue: filters.planType,
            priceRangeValue: filters.priceRange
          })
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.rating.title"
          }),
          subtitle: filters.minOverallRating ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.rating.subtitle",
            options: {
              count: filters.minOverallRating
            }
          }) : null,
          children: /*#__PURE__*/_jsx(RatingFilter, {
            disabled: isFilterDisabled('minOverallRating')
            // must be a different name than the desktop RatingFilter
            ,
            name: "mobile_minOverallRating",
            onChange: handleFieldChange,
            value: filters.minOverallRating
          })
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.support.title"
          }),
          subtitle: filters.hasFreeSupport ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.support.subtitle"
          }) : null,
          children: /*#__PURE__*/_jsx(FreeSupportFilter, {
            disabled: isFilterDisabled('hasFreeSupport'),
            checked: !!filters.hasFreeSupport,
            onChange: (field, event) => handleFieldChange(field, event.target.value)
          })
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.providers.title"
          }),
          subtitle: /*#__PURE__*/_jsx(FormattedList, {
            children: filters.providers
          }),
          children: /*#__PURE__*/_jsx(ProviderFilter, {
            disabled: isFilterDisabled('providers'),
            offeringType: offeringType,
            onChange: handleMultiSelectChange('providers'),
            scrollable: false,
            value: filters.providers
          })
        }), /*#__PURE__*/_jsx(MobileFilterFieldPanel, {
          onApply: () => onApplyFilters(filters),
          onClose: () => focusOnFilterPanelButton(),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.assets.filters.searchTags.title"
          }),
          subtitle: /*#__PURE__*/_jsx(FormattedList, {
            children: filters.tags
          }),
          children: /*#__PURE__*/_jsx(SearchTagFilter, {
            disabled: isFilterDisabled('tags'),
            offeringType: offeringType,
            onChange: handleMultiSelectChange('tags'),
            scrollable: false,
            value: filters.tags
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(MobilePanelFooter, {
      children: /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: () => onClearFilters(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.filterPanel.clearAll"
        })
      })
    })]
  });
}